(function () {
  'use strict';

  if (customElements.get('velotric-compare-products')) return;

  const DEFAULT_PRODUCTS = ['14929791189364', '14929865048436', '14962912526708'];
  const TEMPO_DEFAULT_PRODUCTS = ['14975351619956', '7296176488631'];
  const SPEC_ROWS = [
    ['Type', 'type'],
    ['User Height Range', 'user_height_range', true],
    ['Max Range', 'max_range'],
    ['Max Speed', 'max_speed'],
    ['Motor', 'motor_hub'],
    ['Battery', 'battery'],
    ['Pedal Assist', 'pedal_assist'],
    ['Anti-Theft', 'app_apple_find_my'],
    ['Turn Signal', 'turn_signal'],
    ['Fork', 'Fork'],
    ['Sensor', 'sensor'],
    [
      '<div class="td-wrap"><img src="https://cdn.shopify.com/s/files/1/0599/2094/1239/files/UL.svg?v=1740723868" alt="ul"> Safety Certified</div>',
      'ul',
      false,
      true
    ],
    ['Bike Weight', 'bike_weight'],
    ['Max Bike Load Capacity', 'max_bike_load_capacity']
  ];

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function formattedTitle(product) {
    return String(product?.title || '')
      .replace('Ebike', '')
      .replace('Velotric', '')
      .replace(/\b(Fat Tire|Cruiser)\b/g, '')
      .trim();
  }

  class VelotricCompareProducts extends HTMLElement {
    constructor() {
      super();
      this.allProducts = [];
      this.selectedProducts = [];
      this.selectedVariants = {};
      this.storageEnabled = this.dataset.storageEnabled !== 'false';
      this.currentProduct = String(this.dataset.currentProduct || '');
      this.currency = this.dataset.currency || 'USD';
      this.colorsPatterns = this.dataset.colorsPatterns || '';
      this.showSelector = false;
      this.showDifferences = false;
      this.initialized = false;
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.adjustHeight = this.adjustHeight.bind(this);
    }

    connectedCallback() {
      window.addEventListener('resize', this.adjustHeight);
      if (this.initialized) {
        this.adjustHeight();
        return;
      }
      this.initialized = true;
      this.dataset.initialized = 'true';
      this.loadProducts();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.adjustHeight);
    }

    loadProducts() {
      const productSource = this.querySelector('[data-product-json]');
      const specSource = this.querySelector('[data-product-spec-json]');

      try {
        const products = JSON.parse(productSource?.textContent.trim() || '[]') || [];
        const specs = JSON.parse(specSource?.textContent.trim() || '[]') || [];
        this.allProducts = products.map((product, index) => ({
          id: String(product.id),
          title: product.title,
          handle: product.handle,
          variants: product.variants || [],
          specs: specs[index] || {}
        }));

        this.allProducts.forEach((product) => this.initializeVariant(product));
        this.selectedProducts = this.getInitialProducts().filter((id) => this.getProduct(id));
        this.render();
        this.adjustHeight();
      } catch (error) {
        console.error('Comparison JSON Error:', error);
        const loading = this.querySelector('[data-compare-loading]');
        if (loading) loading.hidden = true;
      }
    }

    initializeVariant(product) {
      const variant = product.variants[0];
      if (!variant) return;
      const count = variant.options?.length || 0;
      const selected = { variantId: variant.id };

      if (count === 1) selected.color = variant.option1;
      if (count === 2) {
        selected.size = variant.option1;
        selected.color = variant.option2;
      }
      if (count === 3) {
        selected.style = variant.option1;
        selected.size = variant.option2;
        selected.color = variant.option3;
      }
      this.selectedVariants[product.id] = selected;
    }

    getDefaultProducts() {
      const base = (this.storageEnabled ? DEFAULT_PRODUCTS : TEMPO_DEFAULT_PRODUCTS).map(String);
      if (this.currentProduct && !base.includes(this.currentProduct)) {
        if (this.storageEnabled) base.push(this.currentProduct);
        else if (base.length >= 2) base[1] = this.currentProduct;
        else base.push(this.currentProduct);
      }
      return base;
    }

    getInitialProducts() {
      if (!this.storageEnabled) return this.getDefaultProducts();

      let stored = null;
      try {
        stored = JSON.parse(localStorage.getItem('compare_list'));
      } catch (error) {
        console.warn('Comparison storage data is invalid:', error);
      }

      if (Array.isArray(stored) && stored.length === 1) {
        const list = stored.map(String);
        if (this.currentProduct && !list.includes(this.currentProduct)) list.push(this.currentProduct);
        else return [...new Set([...DEFAULT_PRODUCTS, ...list])];
        return list;
      }
      if (Array.isArray(stored) && stored.length > 1) {
        const list = stored.map(String);
        if (this.currentProduct && !list.includes(this.currentProduct)) list.push(this.currentProduct);
        return list;
      }
      return this.getDefaultProducts();
    }

    getProduct(id) {
      return this.allProducts.find((product) => product.id === String(id));
    }

    getVariant(productId) {
      const product = this.getProduct(productId);
      const variantId = this.selectedVariants[productId]?.variantId;
      return product?.variants.find((variant) => String(variant.id) === String(variantId));
    }

    getAvailableStyles(productId) {
      const product = this.getProduct(productId);
      return [...new Set((product?.variants || []).map((variant) => variant.option1))];
    }

    getAvailableSizes(productId) {
      const product = this.getProduct(productId);
      const count = product?.variants[0]?.options?.length || 0;
      if (count === 2) return [...new Set(product.variants.map((variant) => variant.option1))];
      if (count === 3) {
        const style = this.selectedVariants[productId]?.style;
        return [...new Set(product.variants.filter((variant) => variant.option1 === style).map((variant) => variant.option2))];
      }
      return [];
    }

    getAvailableColors(productId) {
      const product = this.getProduct(productId);
      const count = product?.variants[0]?.options?.length || 0;
      if (count === 1) return [...new Set(product.variants.map((variant) => variant.option1))];
      if (count === 2) {
        const size = this.selectedVariants[productId]?.size;
        return [...new Set(product.variants.filter((variant) => variant.option1 === size).map((variant) => variant.option2))];
      }
      if (count === 3) {
        const style = this.selectedVariants[productId]?.style;
        return [...new Set(product.variants.filter((variant) => variant.option1 === style).map((variant) => variant.option3))];
      }
      return [];
    }

    updateVariant(productId) {
      const product = this.getProduct(productId);
      const selected = this.selectedVariants[productId];
      const count = product?.variants[0]?.options?.length || 0;
      let match;

      if (count === 1) match = product.variants.find((variant) => variant.option1 === selected.color);
      if (count === 2) {
        match = product.variants.find((variant) => variant.option1 === selected.size && variant.option2 === selected.color);
        if (!match) {
          match = product.variants.find((variant) => variant.option1 === selected.size);
          if (match) selected.color = match.option2;
        }
      }
      if (count === 3) {
        match = product.variants.find(
          (variant) => variant.option1 === selected.style && variant.option2 === selected.size && variant.option3 === selected.color
        );
        if (!match) {
          match = product.variants.find((variant) => variant.option1 === selected.style && variant.option2 === selected.size);
          if (match) selected.color = match.option3;
        }
      }
      if (match) selected.variantId = match.id;
    }

    formatPrice(price) {
      if (price == null || price === '') return '';
      return (Number(price) / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }

    renderSelector() {
      return this.allProducts.map((product) => `
        <label class="custom-checkbox-wrapper">
          <input type="checkbox" value="${escapeHtml(product.id)}" data-compare-product-toggle class="custom-checkbox"${this.selectedProducts.includes(product.id) ? ' checked' : ''}>
          <span class="custom-checkbox-mark"></span>
          <span class="checkbox-label-text">${escapeHtml(formattedTitle(product))}</span>
        </label>
      `).join('');
    }

    renderSelect(productId, type, values) {
      const selected = this.selectedVariants[productId]?.[type];
      if (!selected) return '';
      return `
        <div class="custom-select" data-compare-select>
          <button type="button" class="select-trigger" data-compare-select-trigger aria-expanded="false">
            <span>${escapeHtml(selected)}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="7" viewBox="0 0 13 7" fill="none" aria-hidden="true"><path d="M11.8457 0.469482L6.4457 5.86948L1.0457 0.469482" stroke="#121315" stroke-width="1.35"/></svg>
          </button>
          <div class="options" hidden>
            ${values.map((value) => `
              <button type="button" class="option${value === selected ? ' selected' : ''}" data-compare-option data-product-id="${escapeHtml(productId)}" data-option-type="${type}" data-option-value="${escapeHtml(value)}">${escapeHtml(value)}</button>
            `).join('')}
          </div>
        </div>
      `;
    }

    renderModel(productId, index) {
      const product = this.getProduct(productId);
      const variant = this.getVariant(productId) || {};
      const selected = this.selectedVariants[productId] || {};
      const image = variant.featured_image?.src ? `${variant.featured_image.src}&width=1400` : '';
      const compareAt = Number(variant.compare_at_price || 0);
      const price = Number(variant.price || 0);
      const colors = this.getAvailableColors(productId);

      return `
        <td class="${index % 2 === 0 ? 'gray-column ' : ''}td-content_model" data-test="${escapeHtml(this.currentProduct)}">
          <div class="model-wrap">
            <div class="product-card__inner shape shape--landscape" data-product-card-inner>
              <div class="product-card__images-wrapper"><div class="product-card__images multiply-mode">
                <a href="/products/${escapeHtml(product.handle)}"><img class="product-card__img shape__target-image product-card__img--visible multiply-mode__target lazyautosizes lazyloaded" data-sizes="auto" src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" data-hover="null" data-src="${escapeHtml(image)}" width="1" height="1" style="object-position:50.0% 50.0%;" data-parent-fit="cover" data-aspectratio="1.0" sizes="1400px" loading="lazy"></a>
              </div></div>
            </div>
            <div class="variant-selectors">
              <div class="color-options">
                <div>${escapeHtml(selected.color)}</div>
                <div class="color-option">
                  ${colors.map((color) => `
                    <button type="button" class="color-swatch-select-parent${selected.color === color ? ' selected' : ''}" data-compare-color data-product-id="${escapeHtml(productId)}" data-color="${escapeHtml(color)}" title="${escapeHtml(color)}">
                      <shape-swatch class="color-swatch color-swatch--round" data-color="${escapeHtml(color)}" data-ga-action-name="${escapeHtml(String(color).toLowerCase())}" data-section-background="#ffffff" data-colors-patterns="${escapeHtml(this.colorsPatterns)}"></shape-swatch>
                    </button>
                  `).join('')}
                </div>
              </div>
              <div class="other-options">
                ${this.renderSelect(productId, 'style', this.getAvailableStyles(productId))}
                ${this.renderSelect(productId, 'size', this.getAvailableSizes(productId))}
              </div>
            </div>
            <div class="variant-price">
              <span class="variant-price__current">${escapeHtml(this.formatPrice(variant.price))}</span>
              ${compareAt > price ? `<span class="variant-price__compare">${escapeHtml(this.formatPrice(variant.compare_at_price))}</span>` : ''}
            </div>
          </div>
        </td>
      `;
    }

    renderSpecRows() {
      return SPEC_ROWS.map(([label, key, allowHtml, labelHtml]) => {
        const values = this.selectedProducts.map((id, index) => {
          const value = this.getProduct(id)?.specs?.[key] || '';
          return `<td class="${index % 2 === 0 ? 'gray-column ' : ''}td-content">${allowHtml ? value : escapeHtml(value)}</td>`;
        }).join('');
        return `<tr><td class="sticky-col">${labelHtml ? label : escapeHtml(label)}</td>${values}</tr>`;
      }).join('');
    }

    renderTable() {
      const headings = this.selectedProducts.map((id, index) => {
        const product = this.getProduct(id);
        return `<th class="${index % 2 === 0 ? 'gray-column ' : ''}th-content_head"><a href="/products/${escapeHtml(product.handle)}"><div class="compare-title">${escapeHtml(formattedTitle(product))}</div></a></th>`;
      }).join('');
      const models = this.selectedProducts.map((id, index) => this.renderModel(id, index)).join('');
      const footer = this.selectedProducts.map((id) => {
        const product = this.getProduct(id);
        return `<td><a class="product-url" href="/products/${escapeHtml(product.handle)}">Explore Ebike</a></td>`;
      }).join('');

      return `
        <table class="compare-table${this.selectedProducts.length > 4 ? ' scroll-table' : ''}">
          <thead><tr><th class="sticky-col th-content_head">Model</th>${headings}</tr></thead>
          <tbody><tr><td class="sticky-col"></td>${models}</tr>${this.renderSpecRows()}</tbody>
          <tfoot><tr class="view-more-row"><td class="sticky-col"></td>${footer}</tr></tfoot>
        </table>
      `;
    }

    render() {
      const selector = this.querySelector('[data-compare-selector-options]');
      const container = this.querySelector('[data-compare-table]');
      const loading = this.querySelector('[data-compare-loading]');
      if (selector) selector.innerHTML = this.renderSelector();
      if (container) container.innerHTML = this.renderTable();
      if (loading) loading.hidden = true;
      if (container) container.hidden = false;
      this.applyDifferenceFilter();
    }

    saveProducts() {
      if (!this.storageEnabled) return;
      try {
        localStorage.setItem('compare_list', JSON.stringify(this.selectedProducts));
      } catch (error) {
        console.warn('Comparison storage is unavailable:', error);
      }
    }

    handleChange(event) {
      if (event.target.matches('[data-compare-product-toggle]')) {
        const id = String(event.target.value);
        this.selectedProducts = event.target.checked
          ? [...this.selectedProducts, id]
          : this.selectedProducts.filter((productId) => productId !== id);
        this.saveProducts();
        this.render();
      }
      if (event.target.matches('[data-compare-difference]')) {
        this.showDifferences = event.target.checked;
        this.applyDifferenceFilter();
      }
    }

    handleClick(event) {
      const selectorTrigger = event.target.closest('[data-compare-selector-trigger]');
      if (selectorTrigger) {
        this.showSelector = !this.showSelector;
        const modal = this.querySelector('[data-compare-selector-options]');
        if (modal) modal.hidden = !this.showSelector;
        selectorTrigger.setAttribute('aria-expanded', String(this.showSelector));
        return;
      }

      const selectTrigger = event.target.closest('[data-compare-select-trigger]');
      if (selectTrigger) {
        const select = selectTrigger.closest('[data-compare-select]');
        const options = select?.querySelector('.options');
        this.closeSelects(select);
        if (options) {
          options.hidden = !options.hidden;
          selectTrigger.setAttribute('aria-expanded', String(!options.hidden));
        }
        return;
      }

      const option = event.target.closest('[data-compare-option]');
      if (option) {
        const productId = option.dataset.productId;
        const type = option.dataset.optionType;
        const value = option.dataset.optionValue;
        this.selectedVariants[productId][type] = value;
        if (type === 'style' && value === 'High-Step' && this.getProduct(productId)?.handle === 'velotric-nomad-2') {
          this.selectedVariants[productId].size = 'Large';
        }
        this.updateVariant(productId);
        this.render();
        return;
      }

      const color = event.target.closest('[data-compare-color]');
      if (color) {
        const productId = color.dataset.productId;
        this.selectedVariants[productId].color = color.dataset.color;
        this.updateVariant(productId);
        this.render();
      }
    }

    handleDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.showSelector = false;
        const modal = this.querySelector('[data-compare-selector-options]');
        if (modal) modal.hidden = true;
        this.closeSelects();
      }
    }

    closeSelects(except) {
      this.querySelectorAll('[data-compare-select]').forEach((select) => {
        if (select === except) return;
        const options = select.querySelector('.options');
        const trigger = select.querySelector('[data-compare-select-trigger]');
        if (options) options.hidden = true;
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    }

    applyDifferenceFilter() {
      this.querySelectorAll('.compare-table tbody tr').forEach((row, index) => {
        if (index === 0) return;
        const values = Array.from(row.querySelectorAll('td')).slice(1).map((cell) => cell.textContent.trim());
        const allSame = values.length > 0 && values.every((value) => value === values[0]);
        row.hidden = this.showDifferences && allSame;
      });
    }

    adjustHeight() {
      const container = this.querySelector('[data-compare-table]');
      if (!container) return;
      const announcement = document.querySelector('.shopify-section-announcement-bar');
      const available = window.innerHeight - (announcement?.offsetHeight || 0) - (window.innerWidth <= 590 ? 170 : 190);
      container.style.maxHeight = `${available}px`;
    }
  }

  customElements.define('velotric-compare-products', VelotricCompareProducts);

  document.addEventListener('click', (event) => {
    const activeRoot = event.target.closest('velotric-compare-products');
    document.querySelectorAll('velotric-compare-products').forEach((root) => {
      if (!root.allProducts?.length) root.loadProducts();
      if (root === activeRoot) root.handleClick(event);
      else root.handleDocumentClick(event);
    });
  }, true);

  document.addEventListener('change', (event) => {
    const root = event.target.closest('velotric-compare-products');
    if (!root) return;
    if (!root.allProducts?.length) root.loadProducts();
    root.handleChange(event);
  }, true);
})();
